import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, Modal, InputBase, Switch, IconButton, Tooltip } from "@mui/material";
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CustomAxios from "../../utils/CustomAxios";
import { useDispatch, useSelector } from 'react-redux';
import { updateLandingPageAds, updateLoadingLandingPageAds, updateErrorLandingPageAds } from '../../Redux/landing-page-ads';
import { LandingPageAdsTable } from "../../sub_components/landing_page_ads_table";

export default function LandingPageAdsSettings() {
  const toast = useRef(null);
  const dispatch = useDispatch();
  const { landingPageAds, loading, error } = useSelector(state => state.landingPageAds);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewJsonContent, setPreviewJsonContent] = useState(null);
  const previewObjectUrlRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    content_type: "image",
    content_url: "",
    lottie_json_url: "",
    text_content: "",
    background_color: "#ffffff",
    text_color: "#000000",
    border_color: "#cccccc",
    heading: "",
    description: "",
    is_active: true,
    content_file: null,
    content_file_name: "",
    lottie_json_file: null,
    lottie_json_file_name: "",
    // flags for explicit removals when editing
    remove_content: false,
    remove_lottie: false,
  });

  const resetForm = () => {
    setForm({
      name: "",
      content_type: "image",
      content_url: "",
      lottie_json_url: "",
      text_content: "",
      background_color: "#ffffff",
      text_color: "#000000",
      border_color: "#cccccc",
      heading: "",
      description: "",
      is_active: true,
      content_file: null,
      content_file_name: "",
      lottie_json_file: null,
      lottie_json_file_name: "",
      remove_content: false,
      remove_lottie: false,
    });
  };

  const contentTypes = [
    { value: 'video', label: 'Video' },
    { value: 'image', label: 'Image' },
    { value: 'gif', label: 'GIF' },
    { value: 'svg', label: 'SVG' },
    { value: 'json', label: 'JSON' },
    { value: 'text', label: 'Text' }
  ];

  // Filter ads based on search term
  const filteredAds = useMemo(() => {
    if (!searchTerm.trim()) return landingPageAds;
    
    return landingPageAds.filter(ad => 
      ad.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.heading?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.content_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [landingPageAds, searchTerm]);

  const fetchLandingPageAds = async () => {
    dispatch(updateLoadingLandingPageAds(true));
    try {
      const res = await CustomAxios.get('landing_page_ads/');
      const ads = Array.isArray(res?.data) ? res.data : [];
      dispatch(updateLandingPageAds(ads));
    } catch (error) {
      dispatch(updateErrorLandingPageAds(error.message));
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Failed to fetch landing page ads' 
      });
    } finally {
      dispatch(updateLoadingLandingPageAds(false));
    }
  };

  useEffect(() => {
    fetchLandingPageAds();
  }, []);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => { setModalOpen(false); setEditId(null); resetForm(); };

  const openPreview = async (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
    
    // Fetch JSON content if content type is JSON
    if (item.content_type === 'json' && item.content_url) {
      // Only fetch if it's a URL (not a file preview placeholder)
      // If it's a file preview, the JSON content should already be set by openFormPreview
      if (item.content_url && !item.content_url.startsWith('file://')) {
        try {
          const response = await fetch(item.content_url);
          const jsonData = await response.json();
          setPreviewJsonContent(JSON.stringify(jsonData, null, 2));
        } catch (error) {
          setPreviewJsonContent('Failed to load JSON content');
        }
      }
      // If it's a file:// placeholder, previewJsonContent should already be set
    } else {
      // Reset JSON content for non-JSON previews
      setPreviewJsonContent(null);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
    setPreviewJsonContent(null);
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  const openFormPreview = async () => {
    const isFilePresent = Boolean(form.content_file);
    let urlToPreview = form.content_url || '';
    
    // Handle JSON file preview
    if (form.content_type === 'json' && isFilePresent) {
      try {
        const text = await form.content_file.text();
        const jsonData = JSON.parse(text);
        setPreviewJsonContent(JSON.stringify(jsonData, null, 2));
        urlToPreview = 'file://json-preview';
      } catch (error) {
        setPreviewJsonContent('Invalid JSON file');
        urlToPreview = 'file://json-preview';
      }
    } else if (isFilePresent) {
      const objectUrl = URL.createObjectURL(form.content_file);
      previewObjectUrlRef.current = objectUrl;
      urlToPreview = objectUrl;
    }
    const item = {
      name: form.name,
      content_type: form.content_type,
      content_url: urlToPreview,
      background_color: form.background_color,
      border_color: form.border_color,
    };
    openPreview(item);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      name: item.name || "",
      content_type: item.content_type || "image",
      content_url: item.content_url || "",
      lottie_json_url: item.lottie_json_url || "",
      text_content: item.text_content || "",
      background_color: item.background_color || "#ffffff",
      text_color: item.text_color || "#000000",
      border_color: item.border_color || "#cccccc",
      heading: item.heading || "",
      description: item.description || "",
      is_active: !!item.is_active,
      content_file: null,
      content_file_name: "",
      lottie_json_file: null,
      lottie_json_file_name: "",
      remove_content: false,
      remove_lottie: false,
    });
    setModalOpen(true);
  };

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateSwitch = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleContentFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, content_file: file, content_file_name: file ? file.name : "" }));
  };

  const handleRemoveContentFile = () => {
    setForm((prev) => ({ ...prev, content_file: null, content_file_name: "" }));
  };

  const handleRemoveExistingContentUrl = () => {
    setForm((prev) => ({ ...prev, content_url: "", remove_content: true }));
  };

  const handleLottieFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, lottie_json_file: file, lottie_json_file_name: file ? file.name : "" }));
  };

  const handleRemoveLottieFile = () => {
    setForm((prev) => ({ ...prev, lottie_json_file: null, lottie_json_file_name: "" }));
  };

  const handleRemoveExistingLottieUrl = () => {
    setForm((prev) => ({ ...prev, lottie_json_url: "", remove_lottie: true }));
  };

  const saveLandingPageAd = async () => {
    // Yup validation
    try {
      const schema = Yup.object().shape({
        name: Yup.string().trim().required('Name is required'),
        content_type: Yup.string().required('Content type is required'),
        is_active: Yup.boolean().required('Active status is required'),
        background_color: Yup.string().trim().when('content_type', {
          is: (val) => val === 'text',
          then: (s) => s.required('Background color is required for text content'),
          otherwise: (s) => s.notRequired(),
        }),
        text_color: Yup.string().trim().when('content_type', {
          is: (val) => val === 'text',
          then: (s) => s.required('Text color is required for text content'),
          otherwise: (s) => s.notRequired(),
        }),
        border_color: Yup.string().trim().when('content_type', {
          is: (val) => val === 'text',
          then: (s) => s.required('Border color is required for text content'),
          otherwise: (s) => s.notRequired(),
        }),
      });

      await schema.validate(form, { abortEarly: false });
    } catch (validationErrors) {
      if (validationErrors && validationErrors.name === 'ValidationError') {
        const firstError = Array.isArray(validationErrors.errors) && validationErrors.errors.length > 0
          ? validationErrors.errors[0]
          : 'Please fix validation errors';
        toast.current?.show({ severity: 'warn', summary: 'Validation', detail: firstError });
        return;
      }
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fix validation errors' });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      const mappedContentType = form.content_type === 'lottie' ? 'video' : form.content_type;
      
      // Add all form fields
      Object.keys(form).forEach(key => {
        if (
          key !== 'content_file' &&
          key !== 'content_file_name' &&
          key !== 'lottie_json_url' &&
          key !== 'lottie_json_file' &&
          key !== 'lottie_json_file_name' &&
          form[key] !== null && form[key] !== ''
        ) {
          formData.append(key, form[key]);
        }
      });

      // Always pass removal flags when true
      if (form.remove_content) formData.append('remove_content', 'true');
      if (form.remove_lottie) formData.append('remove_lottie', 'true');

      // Override content_type to a backend-supported value when user selects Lottie
      formData.set('content_type', mappedContentType);

      // Add file if present (not required for Lottie)
      if (form.content_file && form.content_type !== 'lottie') {
        formData.append('content_file', form.content_file);
      }

      // Add lottie json if present
      if (form.lottie_json_file) {
        formData.append('lottie_json_file', form.lottie_json_file);
      }

      let response;
      if (editId) {
        response = await CustomAxios.put(`landing_page_ads/${editId}/`, formData);
        toast.current?.show({ 
          severity: 'success', 
          summary: 'Success', 
          detail: 'Landing page ad updated successfully' 
        });
      } else {
        response = await CustomAxios.post('landing_page_ads/', formData);
        toast.current?.show({ 
          severity: 'success', 
          summary: 'Success', 
          detail: 'Landing page ad created successfully' 
        });
      }

      handleClose();
      fetchLandingPageAds();
    } catch (error) {
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: error.response?.data?.detail || 'Failed to save landing page ad' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item) => {
    confirmDialog({
      message: 'Are you sure you want to delete this landing page ad?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await CustomAxios.delete(`landing_page_ads/${item.id}/`);
          toast.current?.show({ 
            severity: 'success', 
            summary: 'Success', 
            detail: 'Landing page ad deleted successfully' 
          });
          fetchLandingPageAds();
        } catch (error) {
          toast.current?.show({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Failed to delete landing page ad' 
          });
        }
      }
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", padding: "30px" }}>
      <Toast ref={toast} />
      <ConfirmDialog />
      
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}>Settings</Typography>
        <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>Landing Page Ads</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Box sx={{ position: "relative" }}>
          <InputBase 
            sx={{ 
              backgroundColor: "white", 
              height: "32px", 
              width: "100%", 
              maxWidth: "268px", 
              borderRadius: "2px", 
              border: "1px solid", 
              borderColor: "secondary.border", 
              padding: "5px 12px 5px 40px", 
              fontSize: "14px", 
              fontWeight: "400", 
              lineHeight: "22px", 
              color: "black" 
            }} 
            placeholder="Search ads" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon sx={{ position: "absolute", left: "12px", top: "5px", width: "20px", height: "20px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Button 
            variant="contained" 
            sx={{ textTransform: "none" }} 
            onClick={handleOpen}
          >
            <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
              Add Items
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", background: "#FFF", padding: "20px" }}>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <LandingPageAdsTable 
            items={filteredAds} 
            onEdit={openEdit} 
            onDelete={handleDelete}
            onView={openPreview}
          />
        )}
      </Box>

      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ zIndex: 1000 }}
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: "900px", height: "82vh", backgroundColor: 'white' }}>
          <Box sx={{ height: "46px important", padding: "12px 24px", backgroundColor: "#EEEEEE", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14", fontWeight: "500", lineHeight: "22px" }}>
              {editId ? 'Edit Landing Page Ad' : 'Add Landing Page Ad'}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2">Active</Typography>
              <Switch checked={form.is_active} name="is_active" onChange={updateSwitch} />
            </Box>
          </Box>
          <Box>
            <Box sx={{ padding: "24px", display: "flex", gap: "16px", flexDirection: "column", height: "calc(80vh - 46px - 60px)", overflowY: "auto" }}>
              <Box sx={{ width: "100%", display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Name<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                  <InputBase name="name" value={form.name} onChange={updateField} placeholder="Enter name"
                    sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Content Type<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                  <select 
                    name="content_type" 
                    value={form.content_type} 
                    onChange={updateField}
                    style={{ 
                      backgroundColor: "white", 
                      height: "32px", 
                      width: "100%", 
                      borderRadius: "2px", 
                      border: "1px solid #ccc", 
                      padding: "5px 12px", 
                      fontSize: "14px", 
                      fontWeight: "400", 
                      lineHeight: "22px", 
                      color: "black" 
                    }}
                  >
                    {contentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>

              <Box sx={{ width: "100%", display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }} >
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Content URL</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <InputBase name="content_url" value={form.content_url} onChange={updateField} placeholder="Enter content URL" disabled={true}
                    sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                  {Boolean(form.content_url) && (
                    <Button color="error" onClick={handleRemoveExistingContentUrl} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Remove</Button>
                  )}
                </Box>
                </Box>
                {form.content_type !== 'lottie' && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Content File</Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 220, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {!form.content_file ? (
                        <>
                        <Box sx={{ position: 'relative', border: '1px dashed', borderColor: 'secondary.border', borderRadius: '6px', px: 1, py: 0.5 }}>
                          <input 
                            type="file" 
                            accept={form.content_type === 'video' ? 'video/*' : form.content_type === 'image' ? 'image/*' : form.content_type === 'gif' ? 'image/gif' : form.content_type === 'svg' ? 'image/svg+xml' : form.content_type === 'json' ? 'application/json' : '*'}
                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} 
                            onChange={handleContentFileChange} 
                          />
                          <Typography sx={{ fontSize: '12px' }}>Upload {form.content_type}</Typography>
                        </Box>
                         {form.content_type !== 'json' && (
                           <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                             <Button 
                               variant="outlined" 
                               onClick={openFormPreview}
                               disabled={!(form.content_file || form.content_url) || (form.content_type === 'text' || form.content_type === 'lottie')}
                               sx={{ textTransform: 'none',height :"30px" }}
                             >
                               Preview
                             </Button>
                           </Box>
                         )}
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '12px' }} title={form.content_file_name}>
                            {form.content_file_name}
                          </Typography>
                          <Button color="error" onClick={handleRemoveContentFile} sx={{ textTransform: 'none' }}>Remove</Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                )}
              </Box>

              {/* Optional Lottie JSON uploader */}
              <Box sx={{ width: "100%", display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }} >
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Lottie JSON URL</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <InputBase name="lottie_json_url" value={form.lottie_json_url} disabled={form.content_type !== 'svg'} placeholder="Auto-filled after upload"
                      sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                    {Boolean(form.lottie_json_url) && (
                      <Button color="error" onClick={handleRemoveExistingLottieUrl} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Remove</Button>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Lottie JSON File (optional)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 220, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {!form.lottie_json_file ? (
                        <Box sx={{ position: 'relative', border: '1px dashed', borderColor: 'secondary.border', borderRadius: '6px', px: 1, py: 0.5, opacity: form.content_type !== 'svg' ? 0.5 : 1 }}>
                          <input 
                            type="file" 
                            accept={'application/json'}
                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: form.content_type !== 'svg' ? 'not-allowed' : 'pointer' }} 
                            disabled={form.content_type !== 'svg'}
                            onChange={handleLottieFileChange} 
                          />
                          <Typography sx={{ fontSize: '12px' }}>Upload Lottie JSON</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '12px' }} title={form.lottie_json_file_name}>
                            {form.lottie_json_file_name}
                          </Typography>
                          <Button color="error" onClick={handleRemoveLottieFile} sx={{ textTransform: 'none' }}>Remove</Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {form.content_type === 'text' && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Text Content</Typography>
                  <InputBase name="text_content" value={form.text_content} onChange={updateField} placeholder="Enter text content" multiline rows={3}
                    sx={{ backgroundColor: "white", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "8px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                </Box>
              )}

              <Box sx={{ width: "100%", display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "32%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Background Color{form.content_type === 'text' ? <span style={{ color: "red", marginLeft: "5px" }}>*</span> : null}</Typography>
                  <InputBase name="background_color" value={form.background_color} onChange={updateField} placeholder="#FFFFFF"
                    sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "32%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Text Color{form.content_type === 'text' ? <span style={{ color: "red", marginLeft: "5px" }}>*</span> : null}</Typography>
                  <InputBase name="text_color" value={form.text_color} onChange={updateField} placeholder="#000000"
                    sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "32%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Border Color{form.content_type === 'text' ? <span style={{ color: "red", marginLeft: "5px" }}>*</span> : null}</Typography>
                  <InputBase name="border_color" value={form.border_color} onChange={updateField} placeholder="#CCCCCC"
                    sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Heading</Typography>
                <InputBase name="heading" value={form.heading} onChange={updateField} placeholder="Enter heading"
                  sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Description</Typography>
                <InputBase name="description" value={form.description} onChange={updateField} placeholder="Enter description" multiline rows={3}
                  sx={{ backgroundColor: "white", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "8px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, padding: "12px 24px", borderTop: "1px solid", borderColor: "secondary.border", height: "60px" }}>
              <Button variant="outlined" onClick={handleClose} sx={{ textTransform: "none" }}>Cancel</Button>
              <Button variant="contained" onClick={saveLandingPageAd} disabled={saving} sx={{ textTransform: "none" }}>{saving ? "Saving..." : "Save"}</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={previewOpen}
        onClose={closePreview}
        aria-labelledby="preview-title"
        aria-describedby="preview-description"
        sx={{ zIndex: 1100 }}
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: "900px", maxHeight: '82vh', backgroundColor: 'white' }}>
          <Box sx={{ padding: "12px 24px", backgroundColor: "#EEEEEE", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14", fontWeight: "500", lineHeight: "22px" }} id="preview-title">
              Preview - {previewItem?.name || ''}
            </Typography>
            <Button variant="outlined" onClick={closePreview} sx={{ textTransform: 'none' }}>Close</Button>
          </Box>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: previewItem?.background_color || '#fff' }}>
            {previewItem?.content_type === 'video' && previewItem?.content_url ? (
              <video src={previewItem.content_url} controls style={{ maxWidth: '100%', maxHeight: '70vh', border: `2px solid ${previewItem?.border_color || '#ccc'}` }} />
            ) : null}

            {(previewItem?.content_type === 'image' || previewItem?.content_type === 'gif') && previewItem?.content_url ? (
              <img 
                src={previewItem.content_url} 
                alt={previewItem?.name || 'preview'} 
                style={{ maxWidth: '100%', maxHeight: '70vh', border: `2px solid ${previewItem?.border_color || '#ccc'}` }} 
              />
            ) : null}

            {previewItem?.content_type === 'svg' && previewItem?.content_url ? (
              <img 
                src={previewItem.content_url} 
                alt={previewItem?.name || 'svg-preview'} 
                style={{ maxWidth: '100%', maxHeight: '70vh', border: `2px solid ${previewItem?.border_color || '#ccc'}` }} 
              />
            ) : null}

            {previewItem?.content_type === 'json' && previewItem?.content_url ? (
              <Box sx={{ width: '100%', maxHeight: '70vh', overflow: 'auto', p: 2, border: `2px solid ${previewItem?.border_color || '#ccc'}`, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', fontFamily: 'monospace' }}>
                  {previewJsonContent || 'Loading JSON content...'}
                </pre>
              </Box>
            ) : null}

            {(!previewItem?.content_url && previewItem && previewItem?.content_type !== 'json') ? (
              <Typography sx={{ color: 'text.secondary' }}>No content URL to preview</Typography>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}